import { Case, CaseStatus } from '../../types';

/**
 * Grouped case structure for display
 */
export interface GroupedCases {
  studentKey: string;  // "LastName, FirstName" for sorting
  studentName: string; // Display name
  cases: Case[];
}

/**
 * Filter state type
 */
export type StatusFilter = CaseStatus | 'all';

/**
 * Sorts cases alphabetically by student last name, then first name (case-insensitive).
 * 
 * Requirements: 2.1
 * 
 * @param cases - Array of cases to sort
 * @returns Sorted array of cases (new array, does not mutate original)
 */
export function sortCasesByStudentName(cases: Case[]): Case[] {
  return [...cases].sort((a, b) => {
    const lastNameA = a.childLastName.toLowerCase();
    const lastNameB = b.childLastName.toLowerCase();
    
    const lastNameComparison = lastNameA.localeCompare(lastNameB);
    if (lastNameComparison !== 0) {
      return lastNameComparison;
    }
    
    // Same last name, compare first names
    const firstNameA = a.childFirstName.toLowerCase();
    const firstNameB = b.childFirstName.toLowerCase();
    return firstNameA.localeCompare(firstNameB);
  });
}

/**
 * Groups cases by student (same childFirstName + childLastName).
 * Assumes cases are already sorted by student name.
 * 
 * Requirements: 2.2
 * 
 * @param cases - Sorted array of cases
 * @returns Array of grouped cases
 */
export function groupCasesByStudent(cases: Case[]): GroupedCases[] {
  const groups: GroupedCases[] = [];
  
  for (const caseItem of cases) {
    const studentKey = `${caseItem.childLastName.toLowerCase()}, ${caseItem.childFirstName.toLowerCase()}`;
    const studentName = `${caseItem.childFirstName} ${caseItem.childLastName}`;
    
    const existingGroup = groups.find(g => g.studentKey === studentKey);
    
    if (existingGroup) {
      existingGroup.cases.push(caseItem);
    } else {
      groups.push({
        studentKey,
        studentName,
        cases: [caseItem],
      });
    }
  }
  
  return groups;
}

/**
 * Filters cases by status.
 * 
 * Requirements: 3.2
 * 
 * @param cases - Array of cases
 * @param status - Status to filter by, or 'all' to return all cases
 * @returns Filtered array of cases
 */
export function filterCasesByStatus(cases: Case[], status: StatusFilter): Case[] {
  if (status === 'all') {
    return cases;
  }
  return cases.filter(c => c.status === status);
}

/**
 * Filters cases by search query (matches student first or last name).
 * Search is case-insensitive.
 * 
 * Requirements: 4.2, 4.3
 * 
 * @param cases - Array of cases
 * @param query - Search query string
 * @returns Filtered array of cases
 */
export function filterCasesBySearch(cases: Case[], query: string): Case[] {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return cases;
  }
  
  const lowerQuery = trimmedQuery.toLowerCase();
  
  return cases.filter(c => {
    const firstName = c.childFirstName.toLowerCase();
    const lastName = c.childLastName.toLowerCase();
    return firstName.includes(lowerQuery) || lastName.includes(lowerQuery);
  });
}
